"use client";

import { useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import type { RecordObjectType } from "@shared/records/object-types";
import type { RecordCondition } from "@shared/records/filter-clause";
import type { RecordSort } from "../types";

export interface ViewDraft {
  name: string;
  scope: "personal" | "shared";
  filters: RecordCondition[];
  sort?: RecordSort;
  columnOrder?: string[];
  columnHidden?: string[];
}

export function useViewActions(objectType: RecordObjectType | undefined) {
  const create = useMutation(api.admin.recordViews.mutations.createView);
  const update = useMutation(api.admin.recordViews.mutations.updateView);
  const remove = useMutation(api.admin.recordViews.mutations.deleteView);
  const setDefault = useMutation(
    api.admin.recordViews.mutations.setDefaultView,
  );
  const duplicate = useMutation(api.admin.recordViews.mutations.duplicateView);

  return {
    createView: (draft: ViewDraft) =>
      create({ objectType: requireObjectType(objectType), ...draft }),
    updateView: (viewId: Id<"recordViews">, draft: Partial<ViewDraft>) => {
      requireObjectType(objectType);
      return update({ viewId, ...draft });
    },
    deleteView: (viewId: Id<"recordViews">) => {
      requireObjectType(objectType);
      return remove({ viewId });
    },
    setDefaultView: (viewId: Id<"recordViews"> | null) =>
      setDefault({ objectType: requireObjectType(objectType), viewId }),
    duplicateView: (viewId: Id<"recordViews">, name: string) => {
      requireObjectType(objectType);
      return duplicate({ viewId, name });
    },
  };
}

function requireObjectType(
  objectType: RecordObjectType | undefined,
): RecordObjectType {
  if (!objectType) {
    throw new Error("Saved views require a registered record object type");
  }
  return objectType;
}
